import React from 'react';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { SvgIconComponent } from '@mui/icons-material';
import ButtonBase from '@mui/material/ButtonBase';
import SvgIcon from '@mui/material/SvgIcon';
import Typography from '@mui/material/Typography';
import Box from '@mui/system/Box';

type SharedSelectorProps = {
  entityLabel?: string;
  selectedItemName?: string | null;
  imgSrc?: string | null;
  imgAlt?: string;
  icon?: SvgIconComponent;
  showCaret?: boolean;
  clickable?: boolean;
  onClick?: () => void;
  ariaDescribedBy?: string;
  emptyStateLabel?: string;
};

const SelectorShell: React.FC<SharedSelectorProps> = ({
  entityLabel,
  selectedItemName,
  imgSrc,
  imgAlt,
  icon,
  showCaret = false,
  clickable = false,
  onClick,
  ariaDescribedBy,
  emptyStateLabel = 'Select',
}) => (
  <ButtonBase
    disableRipple
    className='brand-select__button'
    aria-describedby={ariaDescribedBy}
    onClick={onClick}
    sx={{
      width: '100%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: '8px',
      padding: '6px',
      borderRadius: '10px',
      border: '1px solid rgba(148, 163, 184, 0.42)',
      background:
        'linear-gradient(140deg, rgba(15, 23, 42, 0.5) 0%, rgba(30, 41, 59, 0.38) 55%, rgba(15, 23, 42, 0.54) 100%)',
      color: 'typography.main',
      minHeight: '46px',
      transition: 'border-color .2s ease, background .2s ease, box-shadow .2s ease',
      cursor: clickable ? 'pointer' : 'default',
      textAlign: 'left',
      position: 'relative',
      overflow: 'hidden',
      boxShadow: 'inset 0 1px 0 rgba(255, 255, 255, 0.08), 0 14px 24px -22px rgba(2, 6, 23, 0.95)',
      '&::after': {
        content: '""',
        position: 'absolute',
        left: '1px',
        right: '1px',
        top: '1px',
        height: '50%',
        borderRadius: '9px 9px 14px 14px',
        background: 'linear-gradient(180deg, rgba(255, 255, 255, 0.1), rgba(255, 255, 255, 0))',
        pointerEvents: 'none',
      },
      '&:hover': clickable
        ? {
            borderColor: 'rgba(191, 219, 254, 0.9)',
            background:
              'linear-gradient(140deg, rgba(30, 41, 59, 0.56) 0%, rgba(51, 65, 85, 0.42) 55%, rgba(30, 41, 59, 0.58) 100%)',
            boxShadow:
              'inset 0 1px 0 rgba(255, 255, 255, 0.14), 0 16px 28px -20px rgba(2, 6, 23, 0.98)',
          }
        : {},
    }}
  >
    <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0, flex: 1 }}>
      <Box
        className='brand-select__logo'
        sx={{
          width: '34px',
          height: '34px',
          borderRadius: '9px',
          overflow: 'hidden',
          flexShrink: 0,
          background:
            'linear-gradient(145deg, rgba(255, 255, 255, 0.2) 0%, rgba(148, 163, 184, 0.14) 45%, rgba(15, 23, 42, 0.28) 100%)',
          border: '1px solid rgba(191, 219, 254, 0.45)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow:
            'inset 0 1px 0 rgba(255, 255, 255, 0.18), 0 10px 16px -12px rgba(2, 6, 23, 0.95)',
          '& img': {
            width: '84%',
            height: '84%',
            objectFit: 'contain',
            filter: 'drop-shadow(0 2px 2px rgba(2, 6, 23, 0.42))',
          },
          '& .MuiSvgIcon-root': {
            width: '20px',
            height: '20px',
            color: '#f8fafc',
          },
        }}
      >
        {imgSrc ? (
          <img src={imgSrc} alt={imgAlt || ''} />
        ) : icon ? (
          <SvgIcon component={icon} />
        ) : null}
      </Box>
      <Box className='brand-select__text' sx={{ minWidth: 0 }}>
        {entityLabel && (
          <Typography
            className='brand-select__meta'
            component='p'
            sx={{
              color: 'rgba(226, 232, 240, 0.84)',
              fontSize: '9px',
              textTransform: 'uppercase',
              letterSpacing: '0.08em',
              lineHeight: 1.1,
              fontWeight: 700,
            }}
          >
            {entityLabel}
          </Typography>
        )}
        <Typography
          component='p'
          sx={{
            color: 'typography.main',
            fontSize: '12px',
            fontWeight: 700,
            lineHeight: 1.25,
            whiteSpace: 'nowrap',
            textOverflow: 'ellipsis',
            overflow: 'hidden',
          }}
        >
          {selectedItemName || emptyStateLabel}
        </Typography>
      </Box>
    </Box>
    {showCaret && (
      <Box
        className='brand-select__caret'
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'typography.main',
          width: '20px',
          height: '20px',
          borderRadius: '999px',
          background: 'rgba(15, 23, 42, 0.35)',
          border: '1px solid rgba(191, 219, 254, 0.28)',
        }}
      >
        <ArrowDropDownIcon fontSize='small' />
      </Box>
    )}
  </ButtonBase>
);

/**
 * If we only have one predefined item, no select allowed.
 */
interface SingleSelectItemProps {
  imgSrc?: string | null;
  imgAlt: string;
  selectedItemName?: string | null;
  entityLabel?: string;
}

export const SingleSelectItem: React.FC<SingleSelectItemProps> = ({
  imgSrc,
  imgAlt,
  selectedItemName,
  entityLabel,
}) => (
  <SelectorShell
    entityLabel={entityLabel}
    selectedItemName={selectedItemName}
    imgSrc={imgSrc}
    imgAlt={imgAlt}
    clickable={false}
  />
);

/**
 * If we have a select list with nothing selected.
 */
interface MultiselectItemProps {
  icon: SvgIconComponent;
  handleClick: () => void;
  id?: string;
  label: string;
  entityLabel?: string;
}

export const MultiSelectItem: React.FC<MultiselectItemProps> = ({
  icon,
  handleClick,
  id,
  label,
  entityLabel,
}) => (
  <SelectorShell
    icon={icon}
    onClick={handleClick}
    ariaDescribedBy={id}
    clickable
    showCaret
    entityLabel={entityLabel}
    emptyStateLabel={label}
  />
);

/**
 * If we have a select list with one item selected.
 */
interface MultiSelectPredefinedItemProps extends SingleSelectItemProps {
  handleClick: () => void;
}

export const MultiSelectPredefinedItem: React.FC<MultiSelectPredefinedItemProps> = ({
  imgSrc,
  imgAlt,
  selectedItemName,
  handleClick,
  entityLabel,
}) => (
  <SelectorShell
    entityLabel={entityLabel}
    selectedItemName={selectedItemName}
    imgSrc={imgSrc}
    imgAlt={imgAlt}
    onClick={handleClick}
    clickable
    showCaret
  />
);
